import { useEffect, useRef } from 'react';

import type { ChargingCardsSchemaType } from '@/components/steps/ChargingCardsStep';
import { trackFlowComplete, trackStepAbandonment, trackStepView } from '@/features/onboarding/utils/analytics';

export const useAnalytics = (selectedStep: number, completedProcess: boolean) => {
    const stepLabels = ['charging_cards', 'invoicing_address', 'shipping_address', 'summary'];

    // The beforeunload listener is registered once on mount, so it would otherwise capture a stale
    // `completedProcess`/`selectedStep`. Refs keep the handler reading the latest values, preventing
    // a false abandonment event from firing after the onboarding is completed (e.g. when the
    // completion screen navigates away via the marketplace/overview buttons).
    const completedProcessRef = useRef(completedProcess);
    const selectedStepRef = useRef(selectedStep);

    useEffect(() => {
        completedProcessRef.current = completedProcess;
        selectedStepRef.current = selectedStep;
    }, [completedProcess, selectedStep]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (!completedProcessRef.current) {
                const step = selectedStepRef.current;
                trackStepAbandonment(stepLabels[step]);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const handleStepView = () => {
        if (selectedStep >= 0) {
            trackStepView(stepLabels[selectedStep]);
        }
    };

    const handleFlowComplete = (states: { chargingCardsState: ChargingCardsSchemaType }) => {
        const hasCustomization = states.chargingCardsState.cardsListCustomization.some(
            card => card.line1 || card.line2
        );
        trackFlowComplete(states.chargingCardsState.cardsNumber, hasCustomization);
    };

    return {
        handleStepView,
        handleFlowComplete,
    };
};
